import { Button, Grid, GridColumn, GridRow, Modal } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Warnings } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import { getWarningTypeTranslation } from '../../../utils/getWarningTypeTranslation';
import { useTimetableStore } from '../TimetableProvider';
import styles from './ConflictModal.module.scss';

type ConflictEntry = { warning: Warnings[0]; originalIndex: number };

type ConflictGroup = {
  key: string;
  otherCardLabel: string;
  roomConflicts: ConflictEntry[];
  otherConflicts: ConflictEntry[];
};

export const ConflictModal: FC = observer(() => {
  const { t } = useTranslation();
  const pinboardStore = useTimetableStore();

  if (pinboardStore.conflictModal.conflicts === null) return <></>;

  const allConflicts = pinboardStore.conflictModal.conflicts;

  // Group by the other involved card
  const groupMap = new Map<string, ConflictGroup>();

  allConflicts.forEach((warning, originalIndex) => {
    const otherCards = Array.from(warning.data?.involvedCards ?? []);
    const keys = otherCards.length > 0 ? otherCards : ['__unavailable__'];

    keys.forEach((key) => {
      if (!groupMap.has(key)) {
        let label: string;
        if (key === '__unavailable__') {
          label = t('pinboard.conflict.unavailable', 'Nicht verfügbar');
        } else {
          const other = pinboardStore.getCardByUuid(key);
          label = [other?.subject?.name, other?.classes.map((c) => c.name).join(', ')].filter(Boolean).join(' · ');
        }
        groupMap.set(key, { key, otherCardLabel: label, roomConflicts: [], otherConflicts: [] });
      }
      const group = groupMap.get(key)!;
      if (warning.type === 'overlappingRooms') {
        group.roomConflicts.push({ warning, originalIndex });
      } else {
        group.otherConflicts.push({ warning, originalIndex });
      }
    });
  });

  const groups = Array.from(groupMap.values());
  const hasOtherConflicts = groups.some((g) => g.otherConflicts.length > 0);

  return (
    <Modal
      className={styles['conflict-modal']}
      width='s'
      isOpen={allConflicts.length > 0}
      onRequestClose={() => pinboardStore.abortConflict()}
      title={t('pinboard.conflict.found')}
      hideCloseButton
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      parent={document.fullscreenElement ? document.getElementById('timetable-grid') : null}
    >
      <Grid>
        {groups.map((group) => (
          <GridRow key={group.key} headline={group.otherCardLabel} spacingBottom='none'>
            <GridColumn width={12}>
              <div className={styles.conflicts}>
                {group.roomConflicts.map(({ warning, originalIndex }) => (
                  <div className={styles.conflict} key={originalIndex}>
                    <span>{`${getWarningTypeTranslation(warning.type)} (${warning.data?.name})`}</span>
                    <div className={styles.actions}>
                      <Button
                        height='s'
                        className='mr-3'
                        hierarchy='secondary'
                        onClick={() => pinboardStore.ignoreRoomConflict()}
                      >
                        {t('pinboard.conflict.ignoreRoomConflict')}
                      </Button>
                      <Button
                        height='s'
                        className='mr-3'
                        hierarchy='secondary'
                        onClick={() => pinboardStore.removeOtherRooms(warning, originalIndex)}
                      >
                        {t('pinboard.conflict.removeOtherRooms')}
                      </Button>
                      <Button
                        height='s'
                        hierarchy='secondary'
                        onClick={() => pinboardStore.placeWithoutRoom(warning, originalIndex)}
                      >
                        {t('pinboard.conflict.placeWithoutRoom')}
                      </Button>
                    </div>
                  </div>
                ))}
                {group.otherConflicts.map(({ warning }) => (
                  <div className={styles.conflict} key={warning.data?.uuid}>
                    {`${getWarningTypeTranslation(warning.type)} (${warning.data?.name})`}
                  </div>
                ))}
              </div>
            </GridColumn>
          </GridRow>
        ))}

        {(hasOtherConflicts || groups.length > 0) && (
          <GridRow>
            <GridColumn width={12}>
              <div className={styles.actions}>
                {hasOtherConflicts && (
                  <>
                    <Button className='mr-3' hierarchy='secondary' onClick={() => pinboardStore.ignoreConflict()}>
                      {t('pinboard.conflict.ignore')}
                    </Button>
                    <Button className='mr-3' hierarchy='secondary' onClick={() => console.log('swap')} disabled>
                      {t('pinboard.conflict.swap')}
                    </Button>
                  </>
                )}
                <Button hierarchy='primary' onClick={() => pinboardStore.abortConflict()}>
                  {t('pinboard.conflict.abort')}
                </Button>
              </div>
            </GridColumn>
          </GridRow>
        )}
      </Grid>
    </Modal>
  );
});
